import { isResponseWithData } from 'lib-shared-auth';
import { ResultApiResponse } from 'lib-shared-types-api';

import { api } from 'app/app.api';

import {
  CreateWhatsAppSignupApiArg,
  DeleteIntegrationApiArg,
  ReadAppsApiResponse,
  ReadIntegrationsApiArg,
  ReadIntegrationsApiResponse,
  UpdateIntegrationApiArg,
} from './types/messagingApi.types';
import { transformReadIntegrationsResponse } from './utils/transformResponse';

export const messagingApi = api
  .enhanceEndpoints({
    addTagTypes: ['Messaging'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      readApps: builder.query<ReadAppsApiResponse, void>({
        query: () => ({
          version: 1,
          path: 'messaging/apps',
        }),
      }),
      readIntegrations: builder.query<
        ReadIntegrationsApiResponse,
        ReadIntegrationsApiArg
      >({
        query: ({ appId }) => ({
          version: 1,
          path: `messaging/apps/${appId}/integrations`,
        }),
        transformResponse: transformReadIntegrationsResponse,
      }),
      /**
       * This endpoint handles fetching all apps, as well as all integrations
       * associated with each app, returning an aggregated list of all
       * integrations, which would be more complicated to do in the component.
       */
      readAppsAndIntegrations: builder.query<ReadIntegrationsApiResponse, void>(
        {
          queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
            const res1 = await baseQuery({
              version: 1,
              path: 'messaging/apps',
            });

            if (!isResponseWithData(res1)) {
              return res1;
            }

            const {
              data: { applications },
            } = res1.data as ReadAppsApiResponse;

            const integrations = await Promise.all(
              applications.flatMap(async ({ app_id: appId }) => {
                const res2 = await baseQuery({
                  version: 1,
                  path: `messaging/apps/${appId}/integrations`,
                });

                if (!isResponseWithData(res2)) return [];

                const {
                  data: { integrations },
                } = res2.data as ReadIntegrationsApiResponse;

                return integrations;
              }),
            );

            return {
              data: transformReadIntegrationsResponse({
                data: {
                  integrations: integrations.flat(),
                },
                meta: {},
              }),
            };
          },
          providesTags: [{ type: 'Messaging', id: 'LIST' }],
        },
      ),
      createWhatsAppSignup: builder.mutation<
        ResultApiResponse,
        CreateWhatsAppSignupApiArg
      >({
        query: (body) => ({
          version: 1,
          path: `messaging/whatsapp/signup`,
          method: 'POST',
          body,
        }),
        invalidatesTags: [{ type: 'Messaging', id: 'LIST' }],
      }),
      updateIntegration: builder.mutation<
        ResultApiResponse,
        UpdateIntegrationApiArg
      >({
        query: ({ appId, integrationId, integrationName, queueId }) => ({
          version: 1,
          path: `messaging/apps/${appId}/integrations/${integrationId}`,
          method: 'PUT',
          body: {
            integration_name: integrationName,
            queue_id: queueId,
          },
        }),
        invalidatesTags: [{ type: 'Messaging', id: 'LIST' }],
      }),
      deleteIntegration: builder.mutation<
        ResultApiResponse,
        DeleteIntegrationApiArg
      >({
        query: ({ appId, integrationId }) => ({
          version: 1,
          path: `messaging/apps/${appId}/integrations/${integrationId}`,
          method: 'DELETE',
        }),
        invalidatesTags: [{ type: 'Messaging', id: 'LIST' }],
      }),
    }),
  });
